import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import s from "./layers-menu.module.scss";
import RadioButtons from "./radio-buttons/radio-buttons";
import { Layer } from "../../../interfaces/layer";
import HowToUse from "./how-to-use/how-to-use";
import { DebouncedFunction } from "../useDebounceLayers";
import Checkboxes from "./checkboxes-section/checkboxes";
import { useTranslation } from "react-i18next";
import CustomScrollbar from "../../ui-kit/scrollbar/scrollbar";

const MAX_MENU_HEIGHT = 680;

interface Props {
  is3D: boolean;
  setLayersMenuActive: (val: boolean) => void;
  radioLayers: Layer[];
  checkboxesLayers: Layer[];
  modelCheckboxesLayers: Layer[];
  debounce: DebouncedFunction;
  allSelected: number[];
}

export default function LayersMenu({
  is3D,
  setLayersMenuActive,
  radioLayers,
  checkboxesLayers,
  modelCheckboxesLayers,
  debounce,
  allSelected,
}: Props) {
  const { t, i18n } = useTranslation();
  const [selectedRadioId, setSelectedRadioId] = useState<number>();
  const [selectedCheckboxesId, setSelectedCheckboxesId] = useState<number[]>(
    [],
  );
  const [scrollEnabled, setScrollEnabled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (
      radioLayers.length === 0 &&
      modelCheckboxesLayers.length === 0 &&
      checkboxesLayers.length === 0
    )
      return;

    const el = menuRef.current;
    if (el && el.clientHeight >= MAX_MENU_HEIGHT) {
      setScrollEnabled(true);
    } else {
      setScrollEnabled(false);
    }
  }, [radioLayers, modelCheckboxesLayers, checkboxesLayers]);

  useEffect(() => {
    let selected = null;

    for (let layer of radioLayers) {
      if (allSelected.includes(layer.id)) {
        selected = layer.id;
      }
    }

    if (radioLayers.length > 0 && !selected) {
      setSelectedRadioId(radioLayers[0].id);
    } else if (selected) {
      setSelectedRadioId(selected);
    }
  }, []);

  const handleChangeSelectedItems = (
    id: number,
    checked: boolean,
    type: "radio" | "checkbox",
  ) => {
    if (type === "radio" && checked) {
      setSelectedRadioId(id);
      return;
    }

    if (checked) {
      setSelectedCheckboxesId([...selectedCheckboxesId, id]);
    } else {
      setSelectedCheckboxesId([
        ...selectedCheckboxesId.filter((item: number) => item !== id),
      ]);
    }
  };

  return (
    <div
      style={scrollEnabled ? { height: `${MAX_MENU_HEIGHT}px` } : undefined}
      ref={menuRef}
      className={s.layersMenu}
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      <div className={s.header}>
        <h2 className={s.title}>{t("layersMenu.title")}</h2>
        <button
          className={s.closeButton}
          onClick={() => setLayersMenuActive(false)}
        ></button>
      </div>
      <CustomScrollbar enabled={scrollEnabled} flex={1}>
        {radioLayers.length > 1 && (
          <RadioButtons
            radioLayers={radioLayers}
            debounce={debounce}
            handleChangeSelectedItems={handleChangeSelectedItems}
            selectedRadioId={selectedRadioId!}
            allSelected={allSelected}
          />
        )}
        <Checkboxes
          checkboxesLayers={checkboxesLayers}
          debounce={debounce}
          modelCheckboxesLayers={modelCheckboxesLayers}
          handleChangeSelectedItems={handleChangeSelectedItems}
          allSelected={allSelected}
        />
      </CustomScrollbar>
      <HowToUse is3D={is3D} />
    </div>
  );
}
